import {useState} from "react";
import type {ReactNode} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router-dom";
import {useDashboard} from "../features/workspace/useDashboard";
import {
    addBorrowingSchedule,
    addBorrowingTerms,
    type BorrowingView,
    createBorrowing,
    getBorrowing,
    getBorrowingReconciliation,
    getBorrowingSchedules,
    getBorrowingTerms,
    getBorrowingTimeline,
    linkBorrowingAccount,
    listBorrowings,
    type Money,
    recordBorrowingEvent,
    unlinkBorrowingAccount
} from "../features/borrowing/api";
import {humanError} from "../shared/api/client";
import {ErrorPanel, LoadingPanel} from "../shared/ui/PageState";
import {Modal} from "../shared/ui/Modal";
import {RowMenu} from "../shared/ui/RowMenu";
import {EmptyState} from "../shared/ui/EmptyState";
import {money} from "../shared/lib/format";

function amount(value?: Money | null) {
    return value ? money(value.minor, value.currency as never) : "Not reported";
}

function currentSchedule(schedules?: { versions: { id: string; effectiveFrom: string; effectiveTo?: string | null; provenance: string; schedule?: { payments: { sequence: number; dueAt: string; total: Money; expected?: { component: string; amount: Money }[] }[] } }[]; currentId?: string }) {
    return schedules?.versions?.find((v) => v.id === schedules.currentId);
}

function date(value?: string | null) {
    return value ? new Date(value).toLocaleDateString() : "—";
}

// The replay speaks in stored vocabulary — "insufficient_evidence", "draw",
// "provider_statement". Underscores are a storage decision, so they are
// translated here instead of being shown to a person as if they were English.
const RECONCILIATION_PILLS: Record<string, string> = {
    aligned: "Agrees", disagreement: "Differs", insufficient_evidence: "Not enough evidence",
};
const EVENT_WORDS: Record<string, string> = {
    draw: "Drawdown", charge: "Charge", repayment: "Repayment", refund: "Refund",
    write_off: "Write-off", restructured: "Restructured", refinanced: "Refinanced",
    facility_opened: "Facility opened", limit_changed: "Limit changed", closed: "Closed",
};

function words(value?: string | null) {
    if (!value) return "—";
    const spaced = value.replaceAll("_", " ").replaceAll(".", " ");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function BorrowingsPage({detail = false, currency = "GEL", onNotify}: {
    detail?: boolean;
    currency?: string;
    onNotify: (message: string) => void
}) {
    const dashboard = useDashboard(currency as never);
    const bookID = dashboard.data?.ids.book;
    const {borrowingId} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const list = useQuery({
        queryKey: ["borrowings", bookID],
        queryFn: () => listBorrowings(bookID!),
        enabled: !!bookID
    });
    const item = useQuery({
        queryKey: ["borrowing", bookID, borrowingId],
        queryFn: () => getBorrowing(bookID!, borrowingId!),
        enabled: !!bookID && !!borrowingId && detail
    });
    const timeline = useQuery({
        queryKey: ["borrowing-timeline", bookID, borrowingId],
        queryFn: () => getBorrowingTimeline(bookID!, borrowingId!),
        enabled: !!bookID && !!borrowingId && detail
    });
    const reconciliation = useQuery({
        queryKey: ["borrowing-reconciliation", bookID, borrowingId],
        queryFn: () => getBorrowingReconciliation(bookID!, borrowingId!),
        enabled: !!bookID && !!borrowingId && detail
    });
    const termsHistory = useQuery({queryKey: ["borrowing-terms", bookID, borrowingId], queryFn: () => getBorrowingTerms(bookID!, borrowingId!), enabled: !!bookID && !!borrowingId && detail});
    const scheduleHistory = useQuery({queryKey: ["borrowing-schedules", bookID, borrowingId], queryFn: () => getBorrowingSchedules(bookID!, borrowingId!), enabled: !!bookID && !!borrowingId && detail});
    // A link points at an account id. An id is the database's name for the
    // account, not the person's, so the workspace snapshot every page already
    // holds is used to say it the way the accounts page says it.
    const accountName = (id: string) => dashboard.data?.accounts.find((a) => a.id === id)?.name ?? id;
    const [showCreate, setShowCreate] = useState(false);
    const [showEvent, setShowEvent] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [showSchedule, setShowSchedule] = useState(false);
    const [showLink, setShowLink] = useState(false);
    const [form, setForm] = useState({
        id: "",
        creditor: "",
        currency: "UAH",
        occurredAt: new Date().toISOString().slice(0, 16),
        amount: "",
        type: "repayment",
        provenance: "contractual"
    });
    const create = useMutation({
        mutationFn: () => createBorrowing(bookID!, {
            id: form.id,
            bookId: bookID,
            creditor: form.creditor,
            currency: form.currency,
            openedAt: new Date().toISOString(),
            product: ""
        }), onSuccess: () => {
            setShowCreate(false);
            void queryClient.invalidateQueries({queryKey: ["borrowings"]});
            onNotify("Borrowing created.");
        }
    });
    const event = useMutation({
        mutationFn: () => recordBorrowingEvent(bookID!, borrowingId!, {
            type: form.type,
            eventId: crypto.randomUUID(),
            occurredAt: new Date(form.occurredAt).toISOString(),
            amount: {minor: Math.round(Number(form.amount) * 100), currency: item.data?.currency},
            provenance: form.provenance
        }), onSuccess: () => {
            setShowEvent(false);
            void queryClient.invalidateQueries({queryKey: ["borrowing", bookID, borrowingId]});
            void queryClient.invalidateQueries({queryKey: ["borrowing-timeline", bookID, borrowingId]});
            onNotify("Borrowing event recorded.");
        }
    });
    const terms = useMutation({
        mutationFn: () => addBorrowingTerms(bookID!, borrowingId!, {
            id: crypto.randomUUID(),
            effectiveFrom: new Date(form.occurredAt).toISOString(),
            rate: form.amount,
            provenance: "contractual"
        }), onSuccess: () => {
            setShowTerms(false);
            void queryClient.invalidateQueries({queryKey: ["borrowing", bookID, borrowingId]});
            onNotify("Terms version added.");
        }
    });
    const schedule = useMutation({
        mutationFn: () => addBorrowingSchedule(bookID!, borrowingId!, {
            id: crypto.randomUUID(),
            effectiveFrom: new Date(form.occurredAt).toISOString(),
            schedule: {agreementId: borrowingId, payments: []},
            provenance: "contractual"
        }), onSuccess: () => {
            setShowSchedule(false);
            onNotify("Schedule version added.");
        }
    });
    const link = useMutation({
        mutationFn: () => linkBorrowingAccount(bookID!, borrowingId!, {
            accountId: form.id,
            role: "settlement",
            effectiveFrom: new Date(form.occurredAt).toISOString()
        }), onSuccess: () => {
            setShowLink(false);
            void queryClient.invalidateQueries({queryKey: ["borrowing", bookID, borrowingId]});
            onNotify("Account linked.");
        }
    });
    if (!bookID || list.isPending) {
        return <section className="view"><LoadingPanel message="Loading borrowings…"/></section>;
    }
    if (list.isError) {
        return <section className="view"><ErrorPanel error={list.error} onRetry={() => list.refetch()} what="borrowings"/></section>;
    }
    if (detail && (item.isPending || !item.data)) {
        return <section className="view"><LoadingPanel message="Loading borrowing…"/></section>;
    }
    if (detail && item.isError) {
        return <section className="view"><ErrorPanel error={item.error} onRetry={() => item.refetch()} what="borrowing"/></section>;
    }
    if (detail && item.data) {
        return <><BorrowingDetail view={item.data} timeline={timeline.data?.events ?? []}
                                  reconciliation={reconciliation.data}
                                  terms={termsHistory.data}
                                  schedules={scheduleHistory.data}
                                  accountName={accountName}
                                  onBack={() => navigate("/borrowings")}
                                  onEvent={() => setShowEvent(true)}
                                  onTerms={() => setShowTerms(true)}
                                  onSchedule={() => setShowSchedule(true)}
                                  onLink={() => setShowLink(true)}
                                  onUnlink={(id) => unlinkBorrowingAccount(bookID!, borrowingId!, id).then(() => {
                                      void queryClient.invalidateQueries({queryKey: ["borrowing", bookID, borrowingId]});
                                      onNotify("Account unlinked.");
                                  }).catch((e) => onNotify(humanError(e)))}/>{showEvent ?
            <BorrowingForm title="Record borrowing event" onCancel={() => setShowEvent(false)}
                           onSubmit={() => event.mutate()} pending={event.isPending}
                           error={event.error ? humanError(event.error) : undefined}><label>Type<select value={form.type}
                                                                                                        onChange={(e) => setForm({
                                                                                                            ...form,
                                                                                                            type: e.target.value
                                                                                                        })}>
                <option value="repayment">Repayment</option>
                <option value="drawdown">Drawdown</option>
                <option value="interest">Interest</option>
                <option value="fee">Fee</option>
            </select></label><label>Amount<input type="number" min="0" step="0.01" value={form.amount}
                                                 onChange={(e) => setForm({
                                                     ...form,
                                                     amount: e.target.value
                                                 })}/></label></BorrowingForm> : null}{showTerms ?
            <BorrowingForm title="Add terms version" onCancel={() => setShowTerms(false)} onSubmit={() => terms.mutate()}
                           pending={terms.isPending} error={terms.error ? humanError(terms.error) : undefined}><p
                className="panel-note">Versions use valid-time intervals. Existing history is read-only.</p><label>Effective
                from<input type="datetime-local" value={form.occurredAt}
                           onChange={(e) => setForm({...form, occurredAt: e.target.value})}/></label><label>Rate<input
                value={form.amount} onChange={(e) => setForm({
                ...form,
                amount: e.target.value
            })}/></label></BorrowingForm> : null}{showSchedule ?
            <BorrowingForm title="Add schedule version" onCancel={() => setShowSchedule(false)}
                           onSubmit={() => schedule.mutate()} pending={schedule.isPending}
                           error={schedule.error ? humanError(schedule.error) : undefined}><p className="panel-note">Only
                contractual schedule facts are recorded; paid/overdue status is not inferred.</p><label>Effective from<input
                type="datetime-local" value={form.occurredAt} onChange={(e) => setForm({
                ...form,
                occurredAt: e.target.value
            })}/></label></BorrowingForm> : null}{showLink ?
            <BorrowingForm title="Link account" onCancel={() => setShowLink(false)} onSubmit={() => link.mutate()}
                           pending={link.isPending} error={link.error ? humanError(link.error) : undefined}><label>Account
                ID<input value={form.id}
                         onChange={(e) => setForm({...form, id: e.target.value})}/></label></BorrowingForm> : null}</>;
    }

    return <section className="view" aria-labelledby="borrowings-title">
        <div className="page-heading">
            <div><p className="eyebrow">Borrowing</p><h1 id="borrowings-title">Obligations</h1><p
                className="lede">Replayed facts and provider observations stay separate.</p></div>
            <div className="heading-actions">
                <button className="primary-button" type="button" onClick={() => setShowCreate(true)}>Add borrowing ＋
                </button>
            </div>
        </div>
        {list.data.borrowings.length === 0 ?
            <section className="panel state-panel"><h2>No borrowings yet</h2><p>Record an agreement when you have a
                repayment obligation to track.</p></section> : <div className="table-wrap">
                <table className="data-table">
                    <thead>
                    <tr>
                        <th>Agreement</th>
                        <th className="numeric">Borrowed</th>
                        <th className="numeric">Already paid</th>
                        <th className="numeric">Left to pay</th>
                        <th>Next payment</th>
                    </tr>
                    </thead>
                    <tbody>{list.data.borrowings.map((b) => <tr key={b.agreementId} className="linked-row"
                                                                onClick={() => navigate(`/borrowings/${encodeURIComponent(b.agreementId)}`)}>
                        {/* The row is clickable for a pointer, but a row is not a
                            control: the real link lives in the first cell so the
                            page can be used from a keyboard and read aloud. */}
                        <td><button className="row-link" type="button" onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/borrowings/${encodeURIComponent(b.agreementId)}`);
                        }}>{b.product || b.creditor}</button><small>{b.creditor}</small></td>
                        <td className="numeric">{amount(b.financialOverview.borrowed)}</td>
                        <td className="numeric">{amount(b.financialOverview.actualPayments.amount)}<small>{b.financialOverview.actualPayments.count} payments</small></td>
                        <td className="numeric">{b.financialOverview.remainingCashOutflow ? amount(b.financialOverview.remainingCashOutflow.amount) : b.financialOverview.revolving ? "Scenario needed" : "Unknown"}</td>
                        <td className="nowrap">{b.financialOverview.remainingCashOutflow?.nextPaymentAt ? date(b.financialOverview.remainingCashOutflow.nextPaymentAt) : "—"}</td>
                    </tr>)}</tbody>
                </table>
            </div>}{showCreate ? <div className="panel form-panel"><h2>New borrowing</h2><label>ID<input value={form.id}
                                                                                                         onChange={(e) => setForm({
                                                                                                             ...form,
                                                                                                             id: e.target.value
                                                                                                         })}/></label><label>Creditor<input
        value={form.creditor}
        onChange={(e) => setForm({...form, creditor: e.target.value})}/></label><label>Currency<input
        value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value.toUpperCase()})}/></label>
        <div className="form-actions">
            <button className="secondary-button" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="primary-button" type="button" disabled={create.isPending}
                    onClick={() => create.mutate()}>Create
            </button>
        </div>
        {create.error ? <p className="form-error">{humanError(create.error)}</p> : null}</div> : null}</section>;
}

function BorrowingForm({ title, children, onCancel, onSubmit, pending, error }: { title: string; children: ReactNode; onCancel: () => void; onSubmit: () => void; pending: boolean; error?: string }) {
    return <Modal open onClose={onCancel} title={title} footer={<><button className="secondary-button" type="button" onClick={onCancel}>Cancel</button><button className="primary-button" type="button" disabled={pending} onClick={onSubmit}>Save</button></>}>
        {children}
        {error ? <p className="form-error">{error}</p> : null}
    </Modal>;
}

function BorrowingDetail({view, timeline, reconciliation, terms, schedules, accountName, onBack, onEvent, onTerms, onSchedule, onLink, onUnlink}: {
    view: BorrowingView;
    timeline: {
        eventId: string;
        kind: string;
        occurredAt: string;
        amount: Money;
        paid?: Money | null;
        provenance: string;
        transactionId?: string;
        accountMovementId?: string;
        allocationStatus?: "missing" | "complete";
        allocation?: { component: string; amount: Money }[];
        allocationSource?: string
    }[];
    reconciliation?: {
        replayedPrincipal: Money;
        reportedDebt?: Money | null;
        disagreement?: Money | null;
        status: string;
        debtSource?: string
    };
    terms?: { versions: { id: string; effectiveFrom: string; effectiveTo?: string | null; maturesAt?: string | null; rate?: string; paymentHoliday: boolean; provenance: string }[]; currentId?: string };
    schedules?: { versions: { id: string; effectiveFrom: string; effectiveTo?: string | null; provenance: string; schedule?: { payments: { sequence: number; dueAt: string; total: Money; expected?: { component: string; amount: Money }[] }[] } }[]; currentId?: string };
    accountName: (id: string) => string;
    onBack: () => void;
    onEvent: () => void;
    onTerms: () => void;
    onSchedule: () => void;
    onLink: () => void;
    onUnlink: (id: string) => void
}) {
    const orderedTimeline = [...timeline].sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
    const activeSchedule = currentSchedule(schedules);
    const schedulePayments = activeSchedule?.schedule?.payments ?? [];
    const overview = view.financialOverview;
    const actualRepayments = orderedTimeline.filter((e) => e.kind === "repayment");
    const repaymentRows = [
        ...schedulePayments.map((payment) => ({kind: "expected" as const, date: payment.dueAt, payment})),
        ...actualRepayments.map((event) => ({kind: "actual" as const, date: event.occurredAt, event})),
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const realizedCost = overview.realizedCost.amount ? amount(overview.realizedCost.amount) : "Not confirmed";
    const projectedCost = overview.projectedTotalCost ? amount(overview.projectedTotalCost.amount) : overview.revolving ? "No payoff projection" : "Not available";
    const projectedRepayment = overview.projectedTotalRepayment ? amount(overview.projectedTotalRepayment.amount) : "Not available";
    // API percentages are integer hundredths of a percent (e.g. 1520 = 15.20%).
    const pct = (value?: number) => value == null ? "" : `${(value / 100).toFixed(1)}%`;
    const nextPayment = overview.remainingCashOutflow?.nextPaymentAt
        ? schedulePayments.find((payment) => new Date(payment.dueAt).getTime() === new Date(overview.remainingCashOutflow!.nextPaymentAt!).getTime())
        : undefined;
    return <section className="view" aria-labelledby="borrowing-title">
        <button className="text-button" type="button" onClick={onBack}>← All borrowings</button>
        <div className="page-heading">
            <div><p className="eyebrow">Borrowing agreement</p><h1
                id="borrowing-title">{view.product || view.creditor}</h1><p className="lede">{view.creditor} · {view.currency} ·
                opened {date(view.openedAt)} · matures {date(view.maturesAt)}</p></div>
            {/* One primary action: recording what happened is what a person comes
                here to do. Changing the contract — terms, schedule, which account
                settles it — is rare, so it sits behind one disclosure instead of
                four buttons of equal weight competing for the same glance. */}
            <div className="heading-actions form-actions">
                <button className="primary-button" type="button" onClick={onEvent}>Record event ＋</button>
                <RowMenu label="Agreement settings">
                    <button className="row-menu-item" type="button" onClick={onTerms}>Add terms version</button>
                    <button className="row-menu-item" type="button" onClick={onSchedule}>Add schedule version</button>
                    <button className="row-menu-item" type="button" onClick={onLink}>Link an account</button>
                </RowMenu>
            </div>
        </div>
        <section className="borrowing-story">
            <div className="story-cash panel"><p className="eyebrow">Cash flow</p><div className="story-flow"><div><span>Borrowed</span><strong>{amount(overview.borrowed)}</strong><small>Money received</small></div><div className="story-arrow">→</div><div className="story-paid"><span>Already paid</span><strong>{amount(overview.actualPayments.amount)}</strong><small>{overview.actualPayments.count} payments made</small></div><div className="story-arrow">→</div><div className="story-future"><span>Left to pay</span><strong>{overview.remainingCashOutflow ? amount(overview.remainingCashOutflow.amount) : overview.revolving ? "Scenario needed" : "Unknown"}</strong><small>{overview.remainingCashOutflow ? `${overview.remainingCashOutflow.paymentCount} expected payments` : overview.revolving ? "No payoff projection" : "No current schedule"}</small></div></div>
                {overview.remainingCashOutflow?.nextPaymentAt ? <p className="next-payment"><b>Next payment</b> {nextPayment ? `${amount(nextPayment.total)} · ${date(overview.remainingCashOutflow.nextPaymentAt)}` : `Amount not confirmed · ${date(overview.remainingCashOutflow.nextPaymentAt)}`}<span>Last expected {date(overview.remainingCashOutflow.finalExpectedAt)}</span></p> : null}</div>
            <div className="story-cost panel"><p className="eyebrow">Cost of borrowing</p><div className="cost-grid"><div><span>Paid / estimated cost to date</span><strong className={overview.realizedCost.amount ? "" : "is-unknown"}>{realizedCost}</strong><small>{overview.realizedCost.amount ? `${pct(overview.realizedCost.pctOfOriginalPrincipal)} of amount borrowed` : "Allocation evidence is incomplete"}</small></div><div><span>Projected total cost</span><strong>{projectedCost}</strong><small>{overview.projectedTotalCost ? `${pct(overview.projectedTotalCost.pctOfOriginalPrincipal)} of amount borrowed · current schedule` : overview.revolving ? "Available only with a payoff scenario" : "No deterministic projection"}</small></div><div><span>Projected total repayment</span><strong>{projectedRepayment}</strong><small>{overview.projectedTotalRepayment ? "Based on current schedule" : "Future cash flows unknown"}</small></div></div><p className="panel-note projection-note">{overview.projectedTotalCost ? `Projection based on the current schedule as of ${date(overview.projectionAsOf)}.` : overview.revolving ? "A final cost requires an explicit payoff scenario; historical facts remain separate." : "Future cost is unavailable until a deterministic schedule is recorded."}</p></div>
        </section>
        <section className="section-block repayment-ledger"><div className="section-heading-row"><div><h2>Repayment plan</h2><p className="panel-note">Expected installments and actual repayments share one ledger. Actual payments are not linked to an expected row unless the domain has an explicit association.</p></div><span className="section-kicker">{actualRepayments.length} actual · {schedulePayments.length} expected</span></div>
            {repaymentRows.length ? <div className="table-wrap"><table className="data-table"><thead><tr><th>#</th><th>Date</th><th className="numeric">Expected</th><th className="numeric">Actual paid</th><th>Breakdown</th><th>Status</th></tr></thead><tbody>{repaymentRows.map((row) => {
                if (row.kind === "expected") {
                    const payment = row.payment;
                    const past = new Date(payment.dueAt).getTime() <= new Date(view.asOf).getTime();
                    return <tr key={`expected-${payment.sequence}`} className="expected-row"><td>{payment.sequence}</td><td className="nowrap">{date(payment.dueAt)}</td><td className="numeric">{amount(payment.total)}</td><td className="numeric">—</td><td>{payment.expected?.map((x) => `${words(x.component)} ${amount(x.amount)}`).join(" · ") || "Not stated"}</td><td><span className="status-pill status-planned">{past ? "Expected · not linked" : "Upcoming"}</span></td></tr>;
                }
                const event = row.event;
                const component = (name: string) => event.allocation?.find((x) => x.component === name)?.amount;
                const allocation = event.allocationStatus === "complete" ? [component("principal") && `Principal ${amount(component("principal"))}`, component("interest") && `Interest ${amount(component("interest"))}`, (component("fee.installment") ?? component("fee.service") ?? component("fee")) && `Fees ${amount(component("fee.installment") ?? component("fee.service") ?? component("fee"))}`].filter(Boolean).join(" · ") : "Not confirmed";
                return <tr key={`actual-${event.eventId}`} className="actual-row"><td>—</td><td className="nowrap">{date(event.occurredAt)}</td><td className="numeric">—</td><td className="numeric">{amount(event.paid ?? event.amount)}</td><td>{allocation}</td><td><span className={`status-pill ${event.allocationStatus === "complete" ? "status-fulfilled" : "status-planned"}`}>Paid{event.allocationStatus === "complete" ? "" : " · breakdown unknown"}</span></td></tr>;
            })}</tbody></table></div> : <EmptyState>No actual payments or current expected schedule are available.</EmptyState>}
        </section>
        <section className="section-block secondary-section"><h2>Activity</h2>
            <p className="panel-note">Borrowing facts, newest first. This is supporting evidence; the repayment plan above is the primary view.</p>
            {orderedTimeline.length ? <div className="table-wrap">
                <table className="data-table">
                    <thead>
                    <tr>
                        <th>Date</th>
                        <th>Event</th>
                        <th className="numeric">Applied</th>
                        <th className="numeric">Paid</th>
                        <th>Provenance</th>
                        <th>Source</th>
                    </tr>
                    </thead>
                    <tbody>{orderedTimeline.map((e) => <tr key={e.eventId}>
                        <td className="nowrap">{date(e.occurredAt)}</td>
                        <td><strong>{EVENT_WORDS[e.kind] ?? words(e.kind)}</strong></td>
                        <td className="numeric">{amount(e.amount)}</td>
                        <td className="numeric">{e.paid ? amount(e.paid) : "—"}</td>
                        <td>{words(e.provenance)}</td>
                        <td>{e.accountMovementId ? "Bank movement" : e.transactionId ? "Transaction" : "Entered by hand"}</td>
                    </tr>)}</tbody>
                </table>
            </div> : <EmptyState action={<button className="secondary-button" type="button" onClick={onEvent}>Record
                event ＋</button>}>No activity has been recorded for this agreement yet. A drawdown, a repayment, a fee or an
                interest charge is what the replayed principal is made of.</EmptyState>}</section>
        {view.reportedDebt ? <section className="section-block secondary-section evidence-summary"><h2>Evidence & reconciliation</h2><div className="evidence-line"><div><span>Bank-reported current balance</span><strong>{amount(view.reportedDebt)}</strong><small>Observed from {words(view.debtSource).toLowerCase()}.</small></div><div><span>Reconciliation</span><strong>{RECONCILIATION_PILLS[reconciliation?.status ?? view.reconciliationStatus] ?? words(reconciliation?.status ?? view.reconciliationStatus)}</strong><small>Diagnostic only; it does not change actual payments.</small></div></div></section> : null}
        <section className="section-block"><h2>Terms</h2>
            <p className="panel-note">Reference information as stated by the creditor. The raw rate is not an effective borrowing-cost calculation; history is read-only.</p>
            {terms?.versions?.length ? <div className="table-wrap">
                <table className="data-table">
                    <thead>
                    <tr>
                        <th>Valid from</th>
                        <th>Valid to</th>
                        <th className="numeric">Rate stated by creditor</th>
                        <th>Maturity</th>
                        <th>Payment holiday</th>
                        <th>Provenance</th>
                    </tr>
                    </thead>
                    <tbody>{[...terms.versions].sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime()).map((v) => <tr key={v.id} className={v.id === terms.currentId ? "current-row" : ""}>
                        <td className="nowrap">{date(v.effectiveFrom)}{v.id === terms.currentId ?
                            <small>Current</small> : null}</td>
                        <td className="nowrap">{v.effectiveTo ? date(v.effectiveTo) : "Open"}</td>
                        <td className="numeric">{v.rate || "—"}</td>
                        <td className="nowrap">{date(v.maturesAt)}</td>
                        <td>{v.paymentHoliday ? "Yes" : "No"}</td>
                        <td>{words(v.provenance)}</td>
                    </tr>)}</tbody>
                </table>
            </div> : <EmptyState action={<button className="secondary-button" type="button" onClick={onTerms}>Add terms
                version</button>}>No terms recorded. A terms version — rate, maturity, payment holiday — is what the
                replay reads when it decides what a payment was for.</EmptyState>}</section>
        <section className="section-block"><h2>Schedule history</h2>
            <p className="panel-note">Contractual expectations and their provenance. Paid and overdue are never inferred here.</p>
            {schedules?.versions?.length ? [...(schedules.versions ?? [])].sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime()).map((v) => <div className="table-wrap versioned"
                                                                                      key={v.id}>
                <p className="table-caption"><strong>{v.id === schedules.currentId ? "Current version" : "Historical version"}</strong>
                    <span>{date(v.effectiveFrom)} — {v.effectiveTo ? date(v.effectiveTo) : "open"} · source: {words(v.provenance)}</span>
                </p>
                <table className="data-table">
                    <thead>
                    <tr>
                        <th className="numeric">#</th>
                        <th>Due</th>
                        <th className="numeric">Total</th>
                        <th>Components</th>
                    </tr>
                    </thead>
                    <tbody>{[...(v.schedule?.payments ?? [])].sort((a, b) => new Date(b.dueAt).getTime() - new Date(a.dueAt).getTime()).map((p) => <tr key={p.sequence}>
                        <td className="numeric">{p.sequence}</td>
                        <td className="nowrap">{date(p.dueAt)}</td>
                        <td className="numeric">{amount(p.total)}</td>
                        <td>{p.expected?.map((x) => `${words(x.component)} ${amount(x.amount)}`).join(" · ") || "—"}</td>
                    </tr>)}</tbody>
                </table>
            </div>) : <EmptyState action={<button className="secondary-button" type="button" onClick={onSchedule}>Add
                schedule version</button>}>No schedule recorded. Without one, only what actually happened is known —
                nothing says what was supposed to.</EmptyState>}</section>
        <section className="section-block"><h2>Linked accounts</h2>
            <p className="panel-note">Linking an account is how a payment on it becomes a repayment here.</p>
            {view.linkedAccounts?.length ? <ul className="link-list">{view.linkedAccounts.map((l) => <li
                key={`${l.accountId}-${l.effectiveFrom}`}>
                <div><strong>{accountName(l.accountId)}</strong><small>{words(l.role)} · since {date(l.effectiveFrom)}</small>
                </div>
                <button className="text-button danger" type="button"
                        onClick={() => onUnlink(l.accountId)}>Unlink</button>
            </li>)}</ul> : <EmptyState action={<button className="secondary-button" type="button" onClick={onLink}>Link
                an account</button>}>No account settles this agreement yet.</EmptyState>}</section>
    </section>;
}
